import { locales } from '../i18n'

// Define the metadata structure
export interface PageMetadata {
  title: string
  description: string
  keywords: string[]
  ogImage: string
  ogImageAlt: string
  twitterCardType: 'summary' | 'summary_large_image' | 'app' | 'player'
}

// Define application-wide metadata
export interface SiteMetadata {
  siteUrl: string
  siteName: string
  twitterHandle: string
  facebookAppId?: string
  organizationLogo: string
  defaultOgImage: string
}

// Define the site-wide metadata
export const siteMetadata: SiteMetadata = {
  siteUrl: 'https://auto-scribe.ai',
  siteName: 'Autoscribe',
  twitterHandle: '@autoscribe',
  organizationLogo: '/logo.png',
  defaultOgImage: '/share-image.png',
}

// Define default metadata for each language
// This will be used as fallback and extended by page-specific metadata
export const defaultMetadata: Record<string, PageMetadata> = {
  en: {
    title: 'Autoscribe - AI-Powered Meeting Transcription and Summarization',
    description: 'Autoscribe automatically transcribes, summarizes, and extracts insights from meetings, saving you time and keeping your team aligned.',
    keywords: ['transcription', 'meeting notes', 'AI', 'voice recognition', 'productivity'],
    ogImage: siteMetadata.defaultOgImage,
    ogImageAlt: 'Autoscribe - AI-Powered Meeting Transcription',
    twitterCardType: 'summary_large_image',
  },
  fr: {
    title: 'Autoscribe - Transcription et Résumé de Réunions Alimentés par l\'IA',
    description: 'Autoscribe transcrit, résume et extrait automatiquement des insights de vos réunions, économisant votre temps et maintenant votre équipe alignée.',
    keywords: ['transcription', 'notes de réunion', 'IA', 'reconnaissance vocale', 'productivité'],
    ogImage: siteMetadata.defaultOgImage,
    ogImageAlt: 'Autoscribe - Transcription de Réunions Alimentée par l\'IA',
    twitterCardType: 'summary_large_image',
  },
}

// Specific metadata for each page and language
// Pages are identified by their route pattern without language prefix
export const pageMetadata: Record<string, Record<string, Partial<PageMetadata>>> = {
  // Home page
  '/': {
    en: {
      title: 'Autoscribe - AI-Powered Meeting Transcription and Summarization',
      description: 'Autoscribe automatically transcribes, summarizes, and extracts insights from meetings, saving you time and keeping your team aligned.',
      keywords: ['meeting minutes', 'AI transcription', 'meeting summaries', 'voice recognition', 'productivity tools', 'meeting notes'],
    },
    fr: {
      title: 'Autoscribe - Transcription et Résumé de Réunions Alimentés par l\'IA',
      description: 'Autoscribe transcrit, résume et extrait automatiquement des insights de vos réunions, économisant votre temps et maintenant votre équipe alignée.',
      keywords: ['compte-rendu de réunion', 'transcription IA', 'résumés de réunion', 'reconnaissance vocale', 'outils de productivité', 'notes de réunion'],
    },
  },
  
  // Landing page - detailed metadata optimized for SEO
  '/landing': {
    en: {
      title: 'Autoscribe - Save Time with AI-Generated Meeting Minutes',
      description: 'Let AI craft your meeting minutes. Autoscribe automatically transcribes and summarizes meetings based on your specific needs. Start your free trial today.',
      keywords: ['AI meeting minutes', 'automatic transcription', 'meeting summaries', 'voice recognition', 'productivity tools', 'time-saving software', 'meeting notes'],
      ogImage: '/images/conversations-illustration.svg',
      ogImageAlt: 'Autoscribe AI-powered meeting transcription software interface',
      twitterCardType: 'summary_large_image',
    },
    fr: {
      title: 'Autoscribe - Gagnez du Temps avec des Comptes-Rendus de Réunion Générés par IA',
      description: 'Laissez l\'IA rédiger vos comptes-rendus de réunion. Autoscribe transcrit et résume automatiquement les réunions selon vos besoins spécifiques. Commencez votre essai gratuit aujourd\'hui.',
      keywords: ['comptes-rendus IA', 'transcription automatique', 'résumés de réunion', 'reconnaissance vocale', 'outils de productivité', 'logiciel gain de temps', 'notes de réunion'],
      ogImage: '/images/conversations-illustration.svg',
      ogImageAlt: 'Interface du logiciel de transcription de réunions Autoscribe alimenté par IA',
      twitterCardType: 'summary_large_image',
    },
  },
  
  // Sign-up page
  '/signup': {
    en: {
      title: 'Create Account - Autoscribe',
      description: 'Sign up for Autoscribe and enjoy a 30-day free trial with 10h of audio processing included.',
      keywords: ['sign up', 'create account', 'free trial', 'audio processing', 'meeting transcription'],
      twitterCardType: 'summary',
    },
    fr: {
      title: 'Créer un compte - Autoscribe',
      description: 'Inscrivez-vous à Autoscribe et profitez d\'un essai gratuit de 30 jours avec 10h de traitement audio inclus.',
      keywords: ['inscription', 'créer un compte', 'essai gratuit', 'traitement audio', 'transcription de réunions'],
      twitterCardType: 'summary',
    },
  },
  
  // Pricing page
  '/pricing': {
    en: {
      title: 'Autoscribe Pricing - Plans for Teams of All Sizes',
      description: 'Find the perfect Autoscribe plan for your team. From individuals to enterprises, we have pricing options to fit your needs.',
    },
    fr: {
      title: 'Tarification Autoscribe - Plans pour Équipes de Toutes Tailles',
      description: 'Trouvez le forfait Autoscribe parfait pour votre équipe. Des individuels aux entreprises, nous avons des options tarifaires adaptées à vos besoins.',
    },
  },
  
  // Help center
  '/help': {
    en: {
      title: 'Autoscribe Help Center - Support & Resources',
      description: 'Find answers to common questions and learn how to get the most out of Autoscribe with our comprehensive help resources.',
      keywords: ['help center', 'support', 'FAQ', 'tutorials', 'user guides', 'troubleshooting', 'documentation'],
      ogImage: '/images/help-center.jpg',
      ogImageAlt: 'Autoscribe Help Center interface with support resources',
      twitterCardType: 'summary',
    },
    fr: {
      title: 'Centre d\'Aide Autoscribe - Support et Ressources',
      description: 'Trouvez des réponses aux questions courantes et apprenez à tirer le meilleur parti d\'Autoscribe avec nos ressources d\'aide complètes.',
      keywords: ['centre d\'aide', 'support', 'FAQ', 'tutoriels', 'guides utilisateur', 'dépannage', 'documentation'],
      ogImage: '/images/help-center.jpg',
      ogImageAlt: 'Interface du Centre d\'Aide Autoscribe avec ressources de support',
      twitterCardType: 'summary',
    },
  },
  
  // Audio Processing help page
  '/help/audio-processing': {
    en: {
      title: 'How Audio Processing Works | Autoscribe Help Center',
      description: 'Learn how Autoscribe transforms your meeting recordings into accurate transcriptions and insightful summaries with our 5-step audio processing system.',
      keywords: ['audio processing', 'speech recognition', 'transcription technology', 'AI transcription', 'meeting transcription', 'audio enhancement'],
      ogImage: '/images/audio-processing.svg',
      ogImageAlt: 'Autoscribe audio processing workflow diagram showing the 5-step system',
      twitterCardType: 'summary_large_image',
    },
    fr: {
      title: 'Comment Fonctionne le Traitement Audio | Centre d\'Aide Autoscribe',
      description: 'Découvrez comment Autoscribe transforme vos enregistrements de réunion en transcriptions précises et résumés perspicaces avec notre système de traitement audio en 5 étapes.',
      keywords: ['traitement audio', 'reconnaissance vocale', 'technologie de transcription', 'transcription IA', 'transcription de réunions', 'amélioration audio'],
      ogImage: '/images/audio-processing.svg',
      ogImageAlt: 'Diagramme du flux de traitement audio d\'Autoscribe montrant le système en 5 étapes',
      twitterCardType: 'summary_large_image',
    },
  },
  
  // Category-specific metadata templates
  '/help/faq': {
    en: {
      title: 'Frequently Asked Questions | Autoscribe Help Center',
      description: 'Find answers to common questions about Autoscribe, including account management, billing, privacy, and using our AI transcription features.',
      keywords: ['FAQ', 'frequently asked questions', 'help', 'support', 'autoscribe help', 'common questions'],
      twitterCardType: 'summary',
    },
    fr: {
      title: 'Foire Aux Questions | Centre d\'Aide Autoscribe',
      description: 'Trouvez des réponses aux questions fréquentes sur Autoscribe, notamment la gestion de compte, la facturation, la confidentialité et l\'utilisation de nos fonctionnalités de transcription IA.',
      keywords: ['FAQ', 'foire aux questions', 'aide', 'support', 'aide autoscribe', 'questions courantes'],
      twitterCardType: 'summary',
    },
  },
  
  '/help/data-processing': {
    en: {
      title: 'Data Processing & Privacy | Autoscribe Help Center',
      description: 'Learn about how Autoscribe handles your data, our privacy practices, GDPR compliance, and security measures to keep your meeting content safe.',
      keywords: ['data privacy', 'GDPR', 'security', 'data processing', 'data protection', 'privacy policy'],
      twitterCardType: 'summary',
    },
    fr: {
      title: 'Traitement des Données et Confidentialité | Centre d\'Aide Autoscribe',
      description: 'Découvrez comment Autoscribe traite vos données, nos pratiques de confidentialité, la conformité au RGPD et nos mesures de sécurité pour protéger le contenu de vos réunions.',
      keywords: ['confidentialité des données', 'RGPD', 'sécurité', 'traitement des données', 'protection des données', 'politique de confidentialité'],
      twitterCardType: 'summary',
    },
  },
  
  '/help/features': {
    en: {
      title: 'Product News & Features | Autoscribe Help Center',
      description: 'Stay up to date with the latest Autoscribe features, updates, and product announcements to get the most out of your transcription service.',
      keywords: ['product updates', 'new features', 'releases', 'product news', 'autoscribe updates', 'feature announcements'],
      twitterCardType: 'summary',
    },
    fr: {
      title: 'Actualités & Fonctionnalités du Produit | Centre d\'Aide Autoscribe',
      description: 'Restez à jour avec les dernières fonctionnalités d\'Autoscribe, les mises à jour et les annonces de produits pour tirer le meilleur parti de votre service de transcription.',
      keywords: ['mises à jour du produit', 'nouvelles fonctionnalités', 'versions', 'actualités du produit', 'mises à jour autoscribe', 'annonces de fonctionnalités'],
      twitterCardType: 'summary',
    },
  },
}

// Function to get merged metadata for a specific page and language
export const getPageMetadata = (path: string, language: string): PageMetadata => {
  // Remove language prefix if present
  const pathSegments = path.split('/')
  const cleanPath = Object.keys(locales).includes(pathSegments[1]) 
    ? '/' + pathSegments.slice(2).join('/')
    : path
  
  // Get the base metadata for the language
  const base = defaultMetadata[language] || defaultMetadata.en
  
  // Look for exact path match first
  if (pageMetadata[cleanPath]?.[language]) {
    return { ...base, ...pageMetadata[cleanPath][language] }
  }
  
  // Try to find the closest match based on path segments
  for (const metadataPath in pageMetadata) {
    if (cleanPath.startsWith(metadataPath) && metadataPath !== '/') {
      return { ...base, ...pageMetadata[metadataPath][language] }
    }
  }
  
  // Default to base metadata if no specific metadata found
  return base
} 